<template>
  <v-card
    v-if="benefits != null && benefits.length > 0"
    class="benefits-section"
    :color="bgColor"
  >
    <div class="item-container px-md-4" :style="cssVars">
      <v-card
        v-for="(benefit, index) in benefits"
        :key="index"
        class="item-card px-2 px-sm-4 px-xl-12 py-2 py-md-6"
        color="transparent"
        flat
      >
        <div class="benefits-container text-break" v-html="benefit" />
      </v-card>
    </div>
  </v-card>
</template>

<script>
export default {
  name: 'Benefits',
  props: {
    isLuxuryView: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    bgColor() {
      if (this.isLuxuryView) {
        return 'primary';
      }
      return 'transparent';
    },
    textColor() {
      if (this.isLuxuryView) {
        return 'white';
      }
      return 'black';
    },
    pgInfo() {
      return this.$store.getters['Account/partnerGroupDetails'];
    },
    benefits() {
      const benefitsArray = [];

      if (this.pgInfo == null) {
        return benefitsArray;
      }

      for (let suffix = 1; suffix <= 3; suffix++) {
        const benefitKey = `GeneralBenefit${suffix}`;
        if (
          this.pgInfo[benefitKey] != null &&
          this.pgInfo[benefitKey].trim() !== ''
        ) {
          benefitsArray.push(this.pgInfo[benefitKey]);
        }
      }

      return benefitsArray;
    },
    breakpointSmAndDown() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    containerWidth() {
      if (this.breakpointSmAndDown) {
        return `${this.benefits.length * 100}%`;
      }
      return '100%';
    },
    itemWidth() {
      if (this.breakpointSmAndDown || !this.benefits.length) {
        return '100%';
      }
      return `${100 / this.benefits.length}%`;
    },
    cssVars() {
      return {
        '--container-width': this.containerWidth,
        '--item-width': this.itemWidth,
      };
    },
  },
  data() {
    return {
      slideAnimation: null,
    };
  },
  watch: {
    breakpointSmAndDown() {
      this.slideControl();
    },
  },
  beforeDestroy() {
    this.stopAutoSlide();
  },
  mounted() {
    this.slideControl();
  },
  methods: {
    slideControl() {
      if (this.breakpointSmAndDown) {
        this.autoSlide();
      } else {
        this.stopAutoSlide();
      }
    },
    autoSlide() {
      const groupItems = this.$el.querySelector('.item-container');

      if (groupItems == null) {
        return;
      }

      const durationPerItem = 3000;
      const totalDuration = this.benefits.length * (durationPerItem + 1000);
      this.slideAnimation = groupItems.animate(this.getKeyFrames(), {
        duration: totalDuration,
        iterations: Infinity,
      });
    },
    stopAutoSlide() {
      if (this.slideAnimation == null) {
        return;
      }
      this.slideAnimation.cancel();
      this.slideAnimation = null;
    },
    getKeyFrames() {
      const keyFrames = [];

      if (!this.benefits.length) {
        return keyFrames;
      }

      // Start frame with 0 offset '0%'
      keyFrames.push({
        transform: 'translateX(0)',
      });

      // The base value to calculate each frame offset, which is 33.33% for 3 items.
      const offsetBase = 1 / this.benefits.length;

      // A small offset between the previous and next frame for switch
      const offsetSwitch = 0.03;

      /* Add all frames (excluding the start and end ones) in between based on the base offset.
      e.g. for 3 items in the list, offsets will be:
        0%
          translateX(0)
        30.33%
          translateX(0)
        33.33%
          translateX(-33.33%)
        63.66%
          translateX(-33.33%)
        66.66%
          translateX(-66.66%)
        96.99%
          translateX(-66.66%)
        99%
          translateX(-66.66%)
        100%
          translateX(0)
      */
      for (let index = 0; index < this.benefits.length; index++) {
        const offset = offsetBase * (index + 1);
        const offsetPrev = offset - offsetSwitch;
        const offsetNext = offset;

        const translateXPrev = -1 * index * offsetBase * 100;
        const translateXNext = -1 * (index + 1) * offsetBase * 100;

        const translateXPrevStr = translateXPrev ? `${translateXPrev}%` : '0';
        const translateXNextStr = translateXNext ? `${translateXNext}%` : '0';

        // Previous frame
        keyFrames.push({
          transform: `translateX(${translateXPrevStr})`,
          offset: offsetPrev,
        });

        /* Next frame, only add if not the last one. */
        if (index < this.benefits.length - 1) {
          keyFrames.push({
            transform: `translateX(${translateXNextStr})`,
            offset: offsetNext,
          });
        }

        /* For the last one, we add the next frame at 99% (close to end) to give
        a quick jum to the beginning and start the cycle again. */
        if (index === this.benefits.length - 1) {
          keyFrames.push({
            transform: `translateX(${translateXPrevStr})`,
            offset: 0.99,
          });
        }
      }

      // End frame with offset 1 '100%'
      keyFrames.push({
        transform: 'translateX(0)',
      });

      return keyFrames;
    },
  },
};
</script>

<style lang="scss" scoped>
@import url('https://fonts.cdnfonts.com/css/bradley-hand-2?styles=30152');

.benefits-container::v-deep * {
  margin-bottom: 0px;

  font-size: 10px;
  text-align: center;

  @media only screen and (min-width: 961px) {
    font-family: 'Bradley Hand', Roboto, 'Helvetica Neue', Helvetica;
    font-size: 18px;
    text-align: start;
  }
}
.benefits-section {
  width: 100%;
  overflow: hidden;
}
.item-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  width: var(--container-width);
}
.item-card {
  box-sizing: border-box;
  width: var(--item-width);
}
</style>
